import type { AxiosError } from 'axios';
import { clearJarvisSession } from '@utils/auth-session';

/**
 * An impersonation session lives at most 8 h from its start (backend#1680):
 * its access tokens last 1 h and refresh until then. Any request, and the
 * refresh itself, answers 401 with one of these codes once it is over:
 * IMPERSONATION_EXPIRED past the deadline, IMPERSONATION_ENDED when it was
 * stopped elsewhere, the impersonator logged out, or they lost the right to
 * impersonate. Mirrors backend domain/master/auth/services — keep in sync.
 */
export const IMPERSONATION_OVER_CODES = ['IMPERSONATION_EXPIRED', 'IMPERSONATION_ENDED'] as const;

/** Query flag the login page reads to explain why the session ended. */
export const IMPERSONATION_ENDED_PARAM = 'switch';
export const IMPERSONATION_ENDED_VALUE = 'ended';

const errorCode = (error: unknown): string | undefined =>
  (error as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;

export const isImpersonationOverError = (error: unknown): boolean =>
  (IMPERSONATION_OVER_CODES as readonly string[]).includes(errorCode(error) ?? '');

let ending = false;

/**
 * Ends the browser's impersonated session: the tokens, the stored staff
 * session and every impersonation key (so the banner goes), and the query
 * cache. Then the login page, which says the switch-user session ended. The
 * staff tokens are not restored: the backend may have ended the session
 * because the staff member logged out or lost the right.
 *
 * The requests that 401 together all land here; only the first acts.
 */
export const endImpersonatedSession = () => {
  if (ending) return;
  ending = true;
  clearJarvisSession();
  window.location.href = `/auth?${IMPERSONATION_ENDED_PARAM}=${IMPERSONATION_ENDED_VALUE}`;
};
